import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import ChatContext from "./context";
import Message from "./Message";
import Arrow from "./arrow";
import { sendRequest } from "./send-message";
import ClearChatButton from "./components/ClearChatButton";

const ChatBox = () => {
  const [prompt, setPrompt] = useState("");
  const {store, updateData } = useContext(ChatContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const storeRef = useRef(store);

  useEffect(() => {
    storeRef.current = store;
  }, [store]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const updateMessages = useCallback((message) => {
    const currentStore = storeRef.current;
    const newData = { ...currentStore };
    const newMessage = {
      messageId: currentStore.messages.length + 1,
      senderName: "Zucky",
      content: message,
      timestamp: new Date().toISOString(),
    };
    newData.messages = [...currentStore.messages, newMessage];
    newData.isLoading = false;
    updateData(newData);
    storeRef.current = newData; // Update ref after state update
  }, [updateData]);

  const checkSessionLimit = () => {
    const SESSION_LIMIT = 1; // $1 per session
    const userMessages = store.messages.filter(msg => msg.senderName === store.name);
    // Estimate: each message costs approximately $0.01-0.02 (rough estimate)
    const estimatedSessionCost = userMessages.length * 0.015;
    return estimatedSessionCost < SESSION_LIMIT;
  };

  const sendMessage = () => {
    if (prompt.trim() === "") {
      return;
    }

    // Check session limit
    if (!checkSessionLimit()) {
      alert("Session limit reached! You've used $1.00 in this session. Please refresh the page to start a new session.");
      return;
    }

    const userPrompt = prompt.trim();
    const currentStore = storeRef.current;
    const newData = { ...currentStore };
    const newMessage = {
      messageId: currentStore.messages.length + 1,
      senderName: currentStore.name,
      content: userPrompt,
      timestamp: new Date().toISOString(),
      originalPrompt: userPrompt, // Store original prompt for regenerate
    };

    newData.messages = [...currentStore.messages, newMessage];
    newData.isLoading = true;
    updateData(newData);
    storeRef.current = newData; // Update ref immediately
    sendRequest(userPrompt, updateMessages, currentStore.messages);
    setPrompt(""); // Clear input after sending
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      sendMessage();
    }
  };

  const messagesContainerRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [store.messages, store.isLoading, scrollToBottom]);

  // Also scroll when typewriter is typing
  useEffect(() => {
    const handleTypewriterUpdate = () => {
      scrollToBottom();
    };
    window.addEventListener('typewriter-update', handleTypewriterUpdate);
    return () => window.removeEventListener('typewriter-update', handleTypewriterUpdate);
  }, [scrollToBottom]);

  // On mobile, if there are messages, make chat messages area independently scrollable
  // Otherwise, let it flow naturally in the parent scrollable container
  const hasMessages = store.messages.length > 0;

  return (
    <div className={`flex flex-col flex-1 md:basis-2/3 w-full bg-gradient-to-b from-[#D5D5D5] to-[#E1DFFF] ${isMobile ? (hasMessages ? 'min-h-[70vh]' : 'min-h-screen') : 'min-h-0'}`}>
      <div 
        ref={messagesContainerRef}
        className={`${isMobile && hasMessages ? 'max-h-[60vh] overflow-y-auto' : isMobile ? 'flex-1' : 'flex-1 overflow-y-auto'} px-4 pt-4 pb-2`}
        style={{ scrollBehavior: 'smooth' }}
      >
        {store.messages.map((message, index) => {
          // Find the original prompt for AI messages (the user message before it)
          const previousUserMessage = store.messages
            .slice(0, index)
            .reverse()
            .find((msg) => msg.senderName === store.name);
          const originalPrompt = previousUserMessage?.originalPrompt || previousUserMessage?.content;

          return (
            <Message
              key={`message-${message.messageId}`}
              name={message.senderName}
              message={message}
              originalPrompt={originalPrompt}
            />
          );
        })}
        {store.isLoading && (
          <div className="flex w-full justify-start items-center space-x-2 px-4 py-8">
            <div className="flex space-x-1.5">
              <div className="w-2.5 h-2.5 bg-gradient-to-b from-[#4940A9] to-[#AE2DC7] rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1.4s' }}></div>
              <div className="w-2.5 h-2.5 bg-gradient-to-b from-[#4940A9] to-[#AE2DC7] rounded-full animate-bounce" style={{ animationDelay: '200ms', animationDuration: '1.4s' }}></div>
              <div className="w-2.5 h-2.5 bg-gradient-to-b from-[#4940A9] to-[#AE2DC7] rounded-full animate-bounce" style={{ animationDelay: '400ms', animationDuration: '1.4s' }}></div>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-row m-2 md:m-4 p-1 flex-shrink-0 items-center gap-2">
        <input
          type="text"
          onChange={(e) => setPrompt(e.target.value)}
          value={prompt}
          placeholder="Write your prompt..."
          className="flex-1 px-2 md:px-3 py-2 h-[42px] md:h-[44px] text-sm md:text-base bg-transparent border-2 border-[#00000026] rounded-[8px]"
          onKeyPress={handleKeyPress}
          disabled={!checkSessionLimit()}
        />
        <button
          className="p-2 h-[42px] md:h-[44px] bg-gradient-to-b from-[#4940A9] to-[#AE2DC7] rounded-[8px] flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          onClick={sendMessage}
          disabled={!checkSessionLimit()}
        >
          <Arrow width={isMobile ? 20 : 25} height={isMobile ? 20 : 25} />
        </button>
        <ClearChatButton />
      </div>
    </div>
  );
};

export default ChatBox;
